import { includesNormalized, normalizeSearchText } from '@/utils/searchText';

/** Translated labels the pure helpers need (resolved by the caller via i18n). */
export interface CountryLabels {
  /**
   * Names for codes/countries `Intl.DisplayNames` cannot resolve, keyed by
   * `countryCode` (`GB-ENG`, …) or, for code-less rows, by the raw English
   * `country` (`World`).
   */
  overrides: Readonly<Record<string, string>>;
  /** Label of the bucket for leagues without any country. */
  other: string;
}

/** Fields the helpers read — satisfied by both `League` and `AdminLeague`. */
export interface CountryLeague {
  id: number;
  name: string;
  country: string | null;
  countryCode?: string | null;
  countryFlagUrl?: string | null;
}

export interface CountryGroup<T extends CountryLeague = CountryLeague> {
  /** Stable, DOM-safe id: `code:<code>`, `name:<country slug>`, or `none`. */
  key: string;
  code: string | null;
  /** Raw (English) country name from the API; null for the "other" bucket. */
  country: string | null;
  /** Localized display name. */
  name: string;
  /** International competitions ("World"): rendered with a trophy, sorted first. */
  international: boolean;
  flagUrl: string | null;
  /** Server order preserved. */
  leagues: T[];
  /** Ids of leagues whose name repeats inside this country (render the season). */
  seasonShownIds: ReadonlySet<number>;
}

const NO_COUNTRY_KEY = 'none';
/** API-Football's country for international competitions. */
const INTERNATIONAL_COUNTRY = 'World';
const ISO_ALPHA2 = /^[A-Z]{2}$/;

const displayNamesCache = new Map<string, Intl.DisplayNames | null>();

function regionNames(lang: string): Intl.DisplayNames | null {
  if (!displayNamesCache.has(lang)) {
    let names: Intl.DisplayNames | null = null;
    try {
      names = new Intl.DisplayNames([lang], { type: 'region' });
    } catch {
      // Invalid locale tag or no Intl.DisplayNames support.
    }
    displayNamesCache.set(lang, names);
  }
  return displayNamesCache.get(lang) ?? null;
}

/**
 * Localized country name: i18n override (non-ISO codes, "World") → the
 * region name from `Intl.DisplayNames` for ISO alpha-2 codes → the raw
 * `country` string. A league without a country gets `labels.other`.
 */
export function localizeCountryName(
  country: string | null,
  code: string | null | undefined,
  lang: string,
  labels: CountryLabels,
): string {
  if (code) {
    const override = labels.overrides[code];
    if (override) return override;
    if (ISO_ALPHA2.test(code)) {
      try {
        const name = regionNames(lang)?.of(code);
        if (name && name !== code) return name;
      } catch {
        // Unknown region code — fall through to the raw name.
      }
    }
  } else if (country) {
    const override = labels.overrides[country];
    if (override) return override;
  }
  return country ?? labels.other;
}

/**
 * Prefixed so a code, a country name and the no-country bucket never collide
 * (e.g. a country literally named "Other"). Whitespace-free, so it is a valid
 * DOM id fragment.
 */
function groupKey(code: string | null, country: string | null): string {
  if (code) return `code:${code}`;
  if (!country) return NO_COUNTRY_KEY;
  const slug = country.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  return `name:${slug || encodeURIComponent(country)}`;
}

function duplicateNameIds(leagues: readonly CountryLeague[]): Set<number> {
  const counts = new Map<string, number>();
  for (const league of leagues) counts.set(league.name, (counts.get(league.name) ?? 0) + 1);
  return new Set(leagues.filter(league => (counts.get(league.name) ?? 0) > 1).map(league => league.id));
}

/** 0 = international ("World"), 1 = any country (with or without a code), 2 = no country. */
function tier(group: CountryGroup<CountryLeague>): number {
  if (group.international) return 0;
  return group.country ? 1 : 2;
}

/**
 * Buckets leagues by country (server order kept inside each bucket) and sorts
 * the buckets: "World" first, then every country (code or not) by localized
 * name with `Intl.Collator(lang)`, the country-less bucket last.
 */
export function buildCountryGroups<T extends CountryLeague>(
  leagues: readonly T[],
  lang: string,
  labels: CountryLabels,
): CountryGroup<T>[] {
  const byKey = new Map<string, CountryGroup<T>>();
  for (const league of leagues) {
    const code = league.countryCode ?? null;
    const country = league.country || null;
    const key = groupKey(code, country);
    let group = byKey.get(key);
    if (!group) {
      group = {
        key,
        code,
        country,
        name: localizeCountryName(country, code, lang, labels),
        international: !code && country === INTERNATIONAL_COUNTRY,
        flagUrl: null,
        leagues: [],
        seasonShownIds: new Set(),
      };
      byKey.set(key, group);
    }
    group.flagUrl ??= league.countryFlagUrl ?? null;
    group.leagues.push(league);
  }

  const collator = new Intl.Collator(lang);
  return Array.from(byKey.values(), group => ({
    ...group,
    seasonShownIds: duplicateNameIds(group.leagues),
  })).sort((a, b) => tier(a) - tier(b) || collator.compare(a.name, b.name));
}

/**
 * Client-side search over localized + English country names and league
 * names. A country hit keeps the whole group; otherwise only matching
 * leagues stay and empty groups drop out. Blank query returns the input.
 */
export function filterCountryGroups<T extends CountryLeague>(
  groups: CountryGroup<T>[],
  query: string,
): CountryGroup<T>[] {
  const needle = normalizeSearchText(query.trim());
  if (!needle) return groups;

  const result: CountryGroup<T>[] = [];
  for (const group of groups) {
    if (includesNormalized(group.name, needle) || includesNormalized(group.country, needle)) {
      result.push(group);
      continue;
    }
    const leagues = group.leagues.filter(league => includesNormalized(league.name, needle));
    if (leagues.length > 0) result.push({ ...group, leagues });
  }
  return result;
}
